import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, Menu, Spin, Typography } from "antd";
import { journalsQueries } from "@/entities/journal";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString } from "@/shared/utils";
import { $journalApi } from "@entities/journal/fetch/fetch-client.ts";
import { useTitleAmendmentMode } from "../../../../model";
import "./developer-representative.scss";
import { RepresentativeWrapper } from "../../../representative-wrapper";
import { RepresentativesSignatureWrapper } from "../../../representatives-signature-wrapper";

export function DeveloperRepresentative() {
  const [key, setKey] = useState(0);
  const { titleId } = useCoordinator();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, isFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const developer = data?.mainJournal?.titleJournal?.developer;

  const { mutate: removeParticipant, isPending } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/representatives",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleClose = () => {
    onClose();
    setKey((prev) => prev + 1);
  };
  const representatives =
    data?.mainJournal?.titleJournal?.developer?.representatives;

  const handleDelete = (id: string) => {
    removeParticipant({
      params: {
        path: {
          titleId,
        },
      },
      body: {
        developer: {
          projectParticipantId: developer?.projectParticipantId,
          representatives: [{ representativeId: id }],
        },
      },
    });
  };

  const menu = (id: string) => (
    <Menu>
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={() => handleDelete(id)}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  return (
    <Spin spinning={isFetching || isPending}>
      <div>
        {canAddData ? (
          <div className="journal-title-block-header journal-title-block-header--spaced">
            <div className="journal-title-section-heading">
              Уполномоченный представитель застройщика
            </div>

            <Can I="edit" a="title_journals">
              <Button
                type="primary"
                onClick={onOpen}
                disabled={!developer?.projectParticipantId || isPending}
              >
                Добавить сотрудника
              </Button>
            </Can>
            <RepresentativeModalOrgs
              key={key}
              organization={{
                representativeType: "developer",
                developerType: developer?.developerType as
                  | "developer"
                  | "OPERATING"
                  | "REGIONAL",
                isConstructionControl: false,
                companyId: developer?.company?.companyId ?? "",
                projectParticipantId: developer?.projectParticipantId ?? "",
                userCompaniesHistoryId:
                  developer?.company?.companyHistoryId ?? "",
              }}
              isOpen={isOpen}
              handleClose={handleClose}
              representativesAlreadyAdded={developer?.representatives}
              enableMultiAdd
              isAddFromOldService
            />
          </div>
        ) : null}
        {representatives
          ? representatives.map((rep, index) => (
              <RepresentativesSignatureWrapper
                representativeType="developer"
                representative={rep}
                developerType={developer?.developerType!}
              >
                <div
                  key={rep.representativeId || index}
                  className="journal-title-rep-card"
                >
                  <div className="journal-title-rep-card__header">
                    <Typography.Title
                      level={5}
                      className="journal-title-rep-card__name"
                    >
                      {fallbackString(
                        `${rep.user?.surname || ""} ${rep.user?.firstName || ""} ${rep.user?.middleName || ""}`,
                      )}
                    </Typography.Title>
                    {rep.representativeId && titleStatus && !isNewAmendment && (
                      <Can I="edit" a="title_journals">
                        <Dropdown
                          overlay={menu(rep?.representativeId)}
                          trigger={["click"]}
                        >
                          <div className="journal-title-menu-trigger">
                            <MoreOneIcon width={24} height={24} />
                          </div>
                        </Dropdown>
                      </Can>
                    )}
                  </div>

                  <RepresentativeWrapper rep={rep} />
                </div>
              </RepresentativesSignatureWrapper>
            ))
          : null}
      </div>
    </Spin>
  );
}
